/**
 * Thin, throw-safe wrappers around `localStorage` with JSON serialization.
 *
 * This module is the single place where storage/JSON errors are swallowed, so
 * the rest of the cache code can assume reads return `T | null` and writes
 * never throw.
 *
 * Satisfies:
 * - Requirement 1.3: a missing stored value is reported as "no cached data".
 * - Requirement 1.4: a read/parse failure is reported as "no cached data" and
 *   never throws.
 * - Requirement 1.5: a write failure (localStorage unavailable or full) never
 *   throws and is a silent no-op.
 */

/**
 * Read and JSON-parse the value stored under `key`.
 *
 * @returns the parsed value, or `null` when the key is missing, when
 *   `localStorage` is unavailable, or when the stored value fails to parse.
 */
export function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null || raw === undefined) {
      // Missing value: treat as no cached data (Req 1.3).
      return null;
    }
    return JSON.parse(raw) as T;
  } catch {
    // Read or parse failure: treat as no cached data, never throw (Req 1.4).
    return null;
  }
}

/**
 * JSON-serialize `value` and write it under `key`.
 *
 * Any failure (serialization error, `localStorage` unavailable or full) is
 * swallowed so the call is a silent no-op (Req 1.5).
 */
export function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Write failure: silent no-op so callers never have to handle it (Req 1.5).
  }
}

/**
 * Remove the value stored under `key`.
 *
 * Any failure is swallowed so the call never throws.
 */
export function removeKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Remove failure: silent no-op, never throw.
  }
}
